/**
 * Agent Command
 * Interactive agent mode for AI-powered conversations
 */

import { Command } from 'commander';
import chalk from 'chalk';
import { initializeAgent, checkAuthentication } from '../agent/utils.js';
import { analyzeProject, createPlan, formatPlan } from '../agent/planning.js';

/**
 * Detect if running under Bun runtime and show informational notice.
 * Ink v6+ works correctly under Bun, so we no longer block execution.
 */
function checkBunRuntime(): void {
    let isBun = false;

    if (typeof process !== 'undefined') {
        const versions = (process as any).versions;
        if (versions?.bun !== undefined) isBun = true;
        if (typeof (globalThis as any).Bun !== 'undefined') isBun = true;
    }

    if (isBun) {
        console.error(chalk.dim('ℹ  Running under Bun runtime'));
    }
}

const TIER_LABELS: Record<string, string> = {
    lite: 'Paean Lite',
    flash: 'Paean Flash',
    pro: 'Paean Pro',
};

export const agentCommand = new Command('agent')
    .description('Start interactive AI agent mode (default command)')
    .option('--no-mcp', 'Disable local MCP server integration')
    .option('-d, --debug', 'Enable debug logging')
    .option('-m, --message <message>', 'Send a single message and exit')
    .option('-p, --plan', 'Show execution plan before proceeding')
    .option('-r, --resume <conversationId>', 'Resume a previous conversation session')
    .option('-t, --tier <tier>', 'Model tier: lite, flash, pro (default: flash)', 'flash')
    .action(async (options) => {
        // Check for Bun runtime
        checkBunRuntime();

        // Validate message if provided
        if (options.message) {
            const message = String(options.message).trim();
            if (!message) {
                console.error(chalk.red('❌ Error: Message cannot be empty.'));
                process.exit(1);
            }
            if (message.length > 10000) {
                console.error(chalk.red('❌ Error: Message is too long (max 10000 characters).'));
                process.exit(1);
            }
            // Update options with validated message
            options.message = message;
        }

        checkAuthentication();

        const debug = options.debug ?? false;
        const enableMcp = options.mcp !== false;
        const showPlan = options.plan ?? false;
        const tier = (['lite', 'flash', 'pro'].includes(options.tier) ? options.tier : 'flash') as 'lite' | 'flash' | 'pro';

        if (debug) {
            console.error(chalk.dim(`  Model tier: ${TIER_LABELS[tier]} (${tier})`));
        }

        const { mcpState, onMcpToolCall, mcpClient } = await initializeAgent({
            mcp: enableMcp,
            debug
        });

        if (showPlan && !options.message) {
            const analysis = analyzeProject();
            const plan = createPlan(analysis, 'Interactive agent session');
            console.log(formatPlan(plan));

            if (plan.requiresConfirmation) {
                // Simple confirmation using readline
                const readline = await import('readline');
                const rl = readline.createInterface({
                    input: process.stdin,
                    output: process.stdout
                });

                const answer = await new Promise<string>((resolve) => {
                    rl.question('Do you want to proceed with this plan? (y/n): ', resolve);
                });
                rl.close();

                if (answer.toLowerCase() !== 'y' && answer.toLowerCase() !== 'yes') {
                    console.log(chalk.yellow('Plan cancelled. Use --plan to review again.'));
                    process.exit(0);
                }
            }
        }

        try {
            const { startChat } = await import('../agent/chat.ink.js');
            await startChat({
                mcpState,
                onMcpToolCall,
                debug,
                initialMessage: options.message,
                resumeConversationId: options.resume,
                modelTier: tier,
            });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            console.error(chalk.red(`\n❌ Error: ${errorMessage}`));
            if (debug) {
                console.error(chalk.dim(`\nStack trace:\n${error instanceof Error ? error.stack : ''}`));
            }
            process.exit(1);
        } finally {
            try {
                await mcpClient?.disconnectAll();
            } catch (cleanupError) {
                if (debug) {
                    console.error(chalk.dim(`Warning: Error during cleanup: ${(cleanupError as Error).message}`));
                }
            }
        }
    });

/**
 * Start agent mode directly (for default command)
 */
export async function runAgentMode(options: {
    mcp?: boolean;
    debug?: boolean;
    message?: string;
    resumeConversationId?: string;
    gatewayEnabled?: boolean;
    gatewayPollInterval?: number;
    gatewaySessionName?: string;
    wechatEnabled?: boolean;
    modelTier?: 'lite' | 'flash' | 'pro';
}): Promise<void> {
    // Check for Bun runtime
    checkBunRuntime();

    checkAuthentication();

    const debug = options.debug ?? false;
    const enableMcp = options.mcp !== false;
    const resumeConversationId = options.resumeConversationId;

    const { mcpState, onMcpToolCall, mcpClient } = await initializeAgent({
        mcp: enableMcp,
        debug
    });

    // Catch unhandled promise rejections to prevent silent crashes
    process.on('unhandledRejection', (reason) => {
        if (debug) {
            console.error(chalk.dim(`\n  Unhandled rejection: ${reason}`));
        }
    });

    // Graceful shutdown guard — prevents concurrent cleanup from racing
    // signals (mirrors Gemini CLI's isShuttingDown pattern).
    let cleanedUp = false;
    const gracefulCleanup = async (signal: string) => {
        if (cleanedUp) return;
        cleanedUp = true;
        if (debug) {
            console.error(chalk.dim(`\n  Received ${signal}, cleaning up...`));
        }

        // Drain stdin to prevent printing garbage on exit
        // (following Gemini CLI best practice)
        if (process.stdin?.isTTY) {
            try {
                process.stdin.resume().removeAllListeners('data').on('data', () => {});
                await new Promise(r => setTimeout(r, 50));
            } catch { /* best effort */ }
        }

        try {
            await mcpClient?.disconnectAll();
        } catch {
            // Best-effort cleanup
        }
        process.exit(signal === 'SIGTERM' ? 143 : 130);
    };

    // Force-exit safety net: if graceful cleanup hangs for >3 seconds, kill
    const forceExitOnSignal = (signal: string) => {
        gracefulCleanup(signal);
        setTimeout(() => {
            console.error(chalk.dim('\n  Cleanup timed out, forcing exit.'));
            process.exit(signal === 'SIGTERM' ? 143 : 130);
        }, 3000).unref();
    };

    process.on('SIGINT', () => forceExitOnSignal('SIGINT'));
    process.on('SIGTERM', () => forceExitOnSignal('SIGTERM'));
    process.on('SIGHUP', () => forceExitOnSignal('SIGHUP'));

    // Initialize WeChat gateway if enabled
    let wechatService: import('../wechat/service.js').WechatGatewayService | undefined;
    if (options.wechatEnabled) {
        const { WechatGatewayService } = await import('../wechat/service.js');
        wechatService = new WechatGatewayService({ debug });
        wechatService.setMcpState(mcpState, onMcpToolCall);
        if (debug) {
            console.error(chalk.dim('[WeChat] Enabled — polling for WeChat messages'));
        }
    }

    try {
        if (wechatService) {
            wechatService.start().catch((err) => {
                console.error(chalk.yellow(`WeChat channel failed to start: ${err instanceof Error ? err.message : err}`));
            });
        }

        const { startChat } = await import('../agent/chat.ink.js');
        await startChat({
            mcpState,
            onMcpToolCall,
            debug,
            initialMessage: options.message,
            resumeConversationId,
            gatewayEnabled: options.gatewayEnabled,
            gatewayPollInterval: options.gatewayPollInterval,
            gatewaySessionName: options.gatewaySessionName,
            wechatService,
            modelTier: options.modelTier,
        });
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        console.error(chalk.red(`\n❌ Fatal error: ${errorMessage}`));
        if (debug) {
            console.error(chalk.dim(`\nStack trace:\n${error instanceof Error ? error.stack : ''}`));
        }
        process.exit(1);
    } finally {
        if (wechatService) {
            await wechatService.stop().catch(() => {});
        }
        if (!cleanedUp) {
            cleanedUp = true;
            try {
                await mcpClient?.disconnectAll();
            } catch (cleanupError) {
                if (debug) {
                    console.error(chalk.dim(`Warning: Error during cleanup: ${(cleanupError as Error).message}`));
                }
            }
        }
    }
}
